import { useRef, useState } from 'react';

/**
 * The draggable seam between the product browser and the cart on the register.
 *
 * The split used to be baked in at one ratio, which is wrong on most screens:
 * a cashier who scans everything wants the ticket wide, one who taps products
 * by hand wants the grid wide, and neither could say so. So the seam moves.
 *
 * Pointer events rather than mouse events — registers run on touch screens, and
 * `setPointerCapture` is what keeps a drag alive once the finger leaves the
 * narrow strip.
 *
 * Direction-aware, and it has to be: the cart is the pane AFTER this one, so it
 * sits right in English and left in Arabic. The measurement is taken from
 * whichever container edge the cart is against, which is one statement that
 * holds in both.
 *
 * The .exe till carries a plain-CSS twin of this file
 * (apps/pos-desktop/src/components/PaneDivider.tsx). The two apps share no
 * build, so the logic is duplicated on purpose — keep them in step by hand.
 */

/** Neither pane may be squeezed below this. */
const MIN_PANE_PX = 260;
/** One arrow-key press, in percentage points. */
const STEP = 2;

/**
 * The trailing pane's share, from a pointer position.
 *
 * `leadIn` and `basis` are what keep the seam UNDER the finger instead of
 * trailing it. The percentage does not address the whole row: half the divider
 * and a grid gap sit between the pointer and the pane's real edge (`leadIn`),
 * and an `fr` track divides only the space left after the divider and both gaps
 * (`basis`). Feeding the raw container width to both — the obvious version —
 * puts the seam tens of pixels from the cursor on a wide screen, which reads as
 * the pane resisting the drag.
 */
export function pctFromPointer(
  rect: { left: number; right: number; width: number },
  clientX: number,
  rtl: boolean,
  leadIn = 0,
  basis = rect.width,
): number | null {
  if (rect.width <= 0 || basis <= 0) return null;
  // Distance from the pointer to whichever container edge the cart is against.
  const px = (rtl ? clientX - rect.left : rect.right - clientX) - leadIn;
  const lo = MIN_PANE_PX;
  const hi = Math.max(lo, basis - MIN_PANE_PX);
  return (Math.min(Math.max(px, lo), hi) / basis) * 100;
}

export function PaneDivider({
  containerRef,
  value,
  onChange,
  onReset,
  label,
}: {
  containerRef: React.RefObject<HTMLElement | null>;
  /** Current share of the container taken by the pane AFTER this divider, 0-100. */
  value: number;
  onChange: (pct: number) => void;
  /** Double-click / Home — back to the shipped ratio. */
  onReset: () => void;
  label: string;
}): JSX.Element {
  const [dragging, setDragging] = useState(false);
  const latest = useRef(value);
  latest.current = value;

  const isRtl = (): boolean => {
    const el = containerRef.current;
    return el ? getComputedStyle(el).direction === 'rtl' : false;
  };

  const nudge = (dir: -1 | 1): void => {
    // Moving the seam towards the cart shrinks it, whichever side that is.
    const delta = (isRtl() ? -dir : dir) * STEP;
    onChange(Math.min(80, Math.max(20, latest.current + delta)));
  };

  const end = (): void => {
    setDragging(false);
    document.body.classList.remove('col-resizing');
  };

  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      title={label}
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={100}
      tabIndex={0}
      data-dragging={dragging ? 'true' : 'false'}
      // A 14px strip so a finger can find it, carrying a hairline and a centred
      // grip — an invisible draggable edge is a feature nobody discovers.
      className="group relative hidden w-3.5 shrink-0 cursor-col-resize touch-none select-none self-stretch outline-none lg:block"
      onPointerDown={(e) => {
        // Primary button only: a right-click here should not start a drag that
        // never ends, because no pointerup follows a context menu.
        if (e.button !== 0) return;
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        setDragging(true);
        document.body.classList.add('col-resizing');
      }}
      onPointerMove={(e) => {
        if (!dragging) return;
        const el = containerRef.current;
        if (!el) return;
        const rect = el.getBoundingClientRect();
        // Measured live rather than hard-coded: the gap is a Tailwind class on
        // the grid and the strip's width a class on this element, and a copy of
        // either number here would rot the moment one of them is restyled.
        const strip = e.currentTarget.getBoundingClientRect().width;
        const gap = parseFloat(getComputedStyle(el).columnGap) || 0;
        const pct = pctFromPointer(rect, e.clientX, isRtl(), strip / 2 + gap, rect.width - strip - gap * 2);
        if (pct !== null) onChange(pct);
      }}
      onPointerUp={(e) => {
        if (!dragging) return;
        e.currentTarget.releasePointerCapture(e.pointerId);
        end();
      }}
      // A cancelled pointer (a system gesture, the window losing focus) never
      // fires pointerup, and without this the page stays in resize-cursor limbo.
      onPointerCancel={end}
      onDoubleClick={onReset}
      onKeyDown={(e) => {
        if (e.key === 'ArrowLeft') { e.preventDefault(); nudge(-1); }
        else if (e.key === 'ArrowRight') { e.preventDefault(); nudge(1); }
        else if (e.key === 'Home') { e.preventDefault(); onReset(); }
      }}
    >
      <span className="pointer-events-none absolute inset-y-0 left-1/2 w-px -translate-x-1/2 bg-border" aria-hidden />
      <span
        className="pointer-events-none absolute left-1/2 top-1/2 h-11 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full bg-border-strong transition-colors group-hover:bg-brand group-focus-visible:bg-brand group-data-[dragging=true]:bg-brand"
        aria-hidden
      />
    </div>
  );
}
